/*
 * YipYip — a clip the shopper pressed play on should have sound
 * ---------------------------------------------------------------------------
 * Pressing play in the dark video band started the clip SILENT, and the only
 * way to hear it was to find the speaker icon in the native controls. Nobody
 * does that; they conclude the video has no audio and move on.
 *
 * The mute is not ours and cannot be turned off in Liquid. Shopify's
 * `media_tag` adds `muted="muted"` whenever it is given `autoplay: true`
 * (measured on the live page), because that is the only way an autoplaying
 * video is allowed to start without a user gesture. The media modal asks for
 * `autoplay: true` so that DeferredMedia will call `play()` for it
 * (global.js:728 plays only when the `autoplay` attribute is there), and
 * inherits the mute along with it.
 *
 * THE BAND NO LONGER COMES THROUGH HERE. yy-product-videos.liquid writes its
 * own <video> now, unmuted, precisely so that nothing has to be changed while
 * the media pipeline is coming up: on iOS, tearing `muted` off a clip that was
 * mid-load and calling play() a second time on top of DeferredMedia's left the
 * audio running and the picture frozen on the poster. An unmuted clip is
 * therefore recognised below and left completely alone.
 *
 * But a poster click IS a user gesture, and a gesture is exactly what browsers
 * require before a video may play aloud. So the attribute is left alone and the
 * element is unmuted after DeferredMedia has appended it, still inside the same
 * click. Listening at the document in the BUBBLE phase is what orders this
 * correctly: DeferredMedia binds to the poster itself (global.js:716), so its
 * handler has already run — and the <video> already exists — by the time the
 * event reaches here.
 *
 * ONLY a real click is treated this way. `playActiveMedia` (media-gallery.js:104)
 * also calls `loadContent`, on plain gallery navigation and on variant changes,
 * and a page that starts talking because someone picked a colour is a worse bug
 * than the one being fixed. Those paths keep Dawn's muted behaviour untouched.
 *
 * The gallery's own in-page player is a different element (`internal-video`,
 * product-thumbnail.liquid) built without `autoplay`, so it never had the
 * `muted` attribute and already played aloud. Nothing here touches it -- and
 * the band's tag is now modelled on it for the same reason.
 */
(function () {
  'use strict';

  // If the browser refuses sound anyway -- an autoplay policy stricter than the
  // gesture we have, or a device rule we cannot see -- fall back to Dawn's
  // behaviour rather than leaving a dead player. A muted clip beats a still one.
  function playMutedInstead(video) {
    video.muted = true;
    var retry = video.play();
    if (retry && typeof retry.catch === 'function') retry.catch(function () {});
  }

  function playAloud(video) {
    if (!video || video.dataset.yySoundHandled === 'true') return;
    video.dataset.yySoundHandled = 'true';

    // Already audible: the band writes its clips unmuted and lets DeferredMedia
    // start them from inside this same click, which is all an unmuted video
    // needs. There is no mute to remove, and a second play() while the first is
    // still resolving is exactly what cost the video track on iOS. Hands off --
    // but fall through to the safety net below, which only acts if the clip is
    // sitting at zero having never started.
    if (video.muted || video.hasAttribute('muted')) {
      video.muted = false;
      video.removeAttribute('muted');

      var started = video.play();
      if (started && typeof started.catch === 'function') {
        started.catch(function () {
          playMutedInstead(video);
        });
      }
    }

    // A rejected promise is the documented signal, but Chrome has a second
    // behaviour it does not report that way: unmuting a video it only allowed
    // BECAUSE it was muted pauses it instead of erroring. One check on the next
    // turn of the loop catches that, and doubles as the net for a band clip
    // whose own play() never took. `ended` and a deliberate pause are excluded
    // so this cannot fight a shopper who stops the clip immediately.
    window.setTimeout(function () {
      if (video.paused && !video.ended && video.currentTime === 0) {
        playMutedInstead(video);
      }
    }, 300);
  }

  document.addEventListener('click', function (event) {
    var poster = event.target.closest && event.target.closest('.deferred-media__poster');
    if (!poster) return;

    var host = poster.closest('deferred-media');
    if (!host) return;

    // DeferredMedia appends the element synchronously in its own handler, so it
    // is normally here already; rAF covers the case where it is not without
    // leaving the user gesture behind.
    var video = host.querySelector('video');
    if (video) {
      playAloud(video);
    } else {
      window.requestAnimationFrame(function () {
        playAloud(host.querySelector('video'));
      });
    }
  });
})();
